import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Loading } from "../components/others/Loading";
import { PageTitle } from "../../_jutemplate/layout/core";
import { useFetchDataForAll } from "../../hooks/useFetchDataForAll";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

// api
import { CropAPI } from "../../api/crop/cropAPI";
import { DiseaseInsectAPI } from "../../api/disease-insect/diseaseInsectAPI";
import { ProductAPI } from "../../api/product/productAPI";
import { DashboardAPI } from "../../api/dashboard/dashboardAPI";

const diseaseInsectApi = new DiseaseInsectAPI();
const cropApi = new CropAPI();
const productApi = new ProductAPI();
const dashboardApi = new DashboardAPI();

const periodOptions = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const orderStatusLabel: { [key: number]: string } = {
  0: "Rejected",
  1: "Accepted",
  2: "Pending",
  3: "Cancelled",
};

// ৳1,250 -> "৳1.3K", ৳1,250,000 -> "৳1.3M"
const formatTaka = (value: number) => {
  const amount = Number(value) || 0;
  if (Math.abs(amount) >= 1000000) return `৳${(amount / 1000000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1000) return `৳${(amount / 1000).toFixed(1)}K`;
  return `৳${amount.toLocaleString()}`;
};

const formatCount = (value: number) => (Number(value) || 0).toLocaleString();

const percentOf = (value: number, total: number) =>
  total > 0 ? Math.round((value / total) * 100) : 0;

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, change, subtitle, icon, color }) => {
  const isPositive = change >= 0;
  return (
    <div className="card shadow-sm border-0 h-100">
      <div className="card-body d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <span className="text-gray-600 fw-semibold fs-6">{title}</span>
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: color + "20",
            }}
          >
            {icon}
          </div>
        </div>
        <div>
          <div className="d-flex align-items-end gap-2 mb-2">
            <span className="fs-2x fw-bold text-gray-900">{value}</span>
            <span
              className={`badge badge-light-${
                isPositive ? "success" : "danger"
              } fs-7 mb-2`}
            >
              {isPositive ? "↑" : "↓"} {Math.abs(change)}%
            </span>
          </div>
          <span className="text-gray-500 fs-7">{subtitle}</span>
        </div>
      </div>
    </div>
  );
};

export const DashBoard: React.FC = () => {
  const [days, setDays] = useState<number>(30);

  // Data fetch custom hook
  const [dashboardData, dashboardDataLoading, , refetchDashboard] =
    useFetchDataForAll(() => dashboardApi.getDashboardSummary(days));

  const handlePeriodChange = (value: number) => {
    setDays(value);
    refetchDashboard(true);
  };

  const [cropData, cropDataLoading] = useFetchDataForAll(() =>
    cropApi.activeCropList()
  );

  const [diseaseInsectData, diseaseInsectDataLoading] = useFetchDataForAll(() =>
    diseaseInsectApi.diseaseInsectActiveList()
  );

  const [productData, productDataLoading] = useFetchDataForAll(() =>
    productApi.activeProductList()
  );

  const summary = dashboardData?.data?.data;
  const period = summary?.period;
  const currentOrders = summary?.orders?.current || {};
  const previousOrders = summary?.orders?.previous || {};
  const dailyCurrent: any[] = summary?.daily?.current || [];
  const dailyPrevious: any[] = summary?.daily?.previous || [];
  const lastSevenDays = dailyCurrent.slice(-7);
  const dealers = summary?.dealers || {};
  const customers = summary?.customers || {};
  const orderByChannel: any[] = summary?.order_by_channel || [];
  const bestSellingProducts: any[] = summary?.best_selling_products || [];
  const recentOrders: any[] = summary?.recent_orders || [];
  const totalOrders = currentOrders.total_orders || 0;

  const revenueChartOptions: ApexOptions = {
    chart: {
      type: "area",
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    colors: ["#12B76A", "#F79009"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
      },
    },
    xaxis: {
      categories: dailyCurrent.map((item) => moment(item.day).format("MMM D")),
      tickAmount: 8,
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        style: { colors: "#667085", fontSize: "12px" },
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#667085", fontSize: "12px" },
        formatter: (val) => formatTaka(val),
      },
    },
    grid: { borderColor: "#F3F4F6", strokeDashArray: 4 },
    legend: { show: true, position: "top", horizontalAlign: "right" },
    tooltip: {
      y: { formatter: (val) => `৳${(Number(val) || 0).toLocaleString()}` },
    },
  };

  // Previous period is aligned day-by-day with the current one (day 1 vs day 1, ...).
  const revenueChartSeries = [
    {
      name: "This Period",
      data: dailyCurrent.map((item) => item.revenue),
    },
    {
      name: "Previous Period",
      data: dailyPrevious.map((item) => item.revenue),
    },
  ];

  // Order Status Chart
  const orderStatusOptions: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 8,
      },
    },
    dataLabels: { enabled: false },
    colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"],
    xaxis: {
      categories: lastSevenDays.map((item) => moment(item.day).format("ddd D")),
      labels: { style: { colors: "#667085", fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        style: { colors: "#667085", fontSize: "12px" },
        formatter: (val) => (Number(val) || 0).toFixed(0),
      },
    },
    grid: { borderColor: "#F3F4F6", strokeDashArray: 4 },
    legend: { show: true, position: "top", horizontalAlign: "right" },
  };

  const orderStatusSeries = [
    { name: "Pending", data: lastSevenDays.map((item) => item.pending) },
    { name: "Accepted", data: lastSevenDays.map((item) => item.accepted) },
    { name: "Delivered", data: lastSevenDays.map((item) => item.delivered) },
    { name: "Returned", data: lastSevenDays.map((item) => item.returned) },
  ];

  // Dealer Performance Radial Chart
  const dealerPerformanceOptions: ApexOptions = {
    chart: {
      type: "radialBar",
      height: 280,
    },
    plotOptions: {
      radialBar: {
        hollow: { size: "65%" },
        dataLabels: {
          name: {
            fontSize: "16px",
            color: "#667085",
            offsetY: -10,
          },
          value: {
            fontSize: "32px",
            fontWeight: "bold",
            color: "#111827",
            offsetY: 5,
          },
        },
      },
    },
    colors: ["#12B76A"],
    labels: ["Active Dealers"],
  };

  const dealerPerformanceSeries = [percentOf(dealers.active, dealers.total)];

  // Order Channel Donut Chart
  const geoDistributionOptions: ApexOptions = {
    chart: {
      type: "donut",
      height: 300,
    },
    labels: orderByChannel.map((item) => item.name),
    noData: { text: "No orders in this period" },
    colors: ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Orders",
              fontSize: "14px",
              color: "#667085",
            },
          },
        },
      },
    },
  };

  const geoDistributionSeries = orderByChannel.map((item) => item.total);

  return (
    <>
      <PageTitle>Dashboard</PageTitle>
      {dashboardDataLoading ? (
        <Loading />
      ) : (
        <div className="row g-4">
          {/* Header Section with Date and Period Filter */}
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-1">Dashboard</h2>
                <p className="text-gray-600 mb-0">
                  {period
                    ? `${moment(period.from).format("MMM D, YYYY")} - ${moment(
                        period.to
                      ).format("MMM D, YYYY")} · Last ${period.days} days`
                    : ""}
                </p>
              </div>
              <select
                className="form-select form-select-sm w-auto"
                value={days}
                onChange={(e) => handlePeriodChange(Number(e.target.value))}
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Metric Cards */}
          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="New Dealers"
              value={formatCount(dealers.current_period)}
              change={dealers.change || 0}
              subtitle={`vs. ${formatCount(
                dealers.previous_period
              )} last period · ${formatCount(dealers.total)} total`}
              color="#3B82F6"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 21H21M4 18H6C6.55228 18 7 17.5523 7 17V7C7 6.44772 6.55228 6 6 6H4C3.44772 6 3 6.44772 3 7V17C3 17.5523 3.44772 18 4 18ZM11 18H13C13.5523 18 14 17.5523 14 17V3C14 2.44772 13.5523 2 13 2H11C10.4477 2 10 2.44772 10 3V17C10 17.5523 10.4477 18 11 18ZM18 18H20C20.5523 18 21 17.5523 21 17V11C21 10.4477 20.5523 10 20 10H18C17.4477 10 17 10.4477 17 11V17C17 17.5523 17.4477 18 18 18Z"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="Orders"
              value={formatCount(totalOrders)}
              change={summary?.orders?.order_change || 0}
              subtitle={`vs. ${formatCount(
                previousOrders.total_orders
              )} last period`}
              color="#10B981"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.707 15.293C4.077 15.923 4.523 17 5.414 17H17M17 17C15.895 17 15 17.895 15 19C15 20.105 15.895 21 17 21C18.105 21 19 20.105 19 19C19 17.895 18.105 17 17 17ZM9 19C9 20.105 8.105 21 7 21C5.895 21 5 20.105 5 19C5 17.895 5.895 17 7 17C8.105 17 9 17.895 9 19Z"
                    stroke="#10B981"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="Revenue"
              value={formatTaka(currentOrders.revenue)}
              change={summary?.orders?.revenue_change || 0}
              subtitle={`vs. ${formatTaka(previousOrders.revenue)} last period`}
              color="#F59E0B"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6"
                    stroke="#F59E0B"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="New Users"
              value={formatCount(customers.current_period)}
              change={customers.change || 0}
              subtitle={`vs. ${formatCount(
                customers.previous_period
              )} last period · ${formatCount(customers.total)} total`}
              color="#8B5CF6"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z"
                    stroke="#8B5CF6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          {/* Total Revenue Chart */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Total Revenue</h3>
                <div className="card-toolbar">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-2x fw-bold text-gray-900">
                      {formatTaka(currentOrders.revenue)}
                    </span>
                    <span
                      className={`badge badge-light-${
                        (summary?.orders?.revenue_change || 0) >= 0
                          ? "success"
                          : "danger"
                      }`}
                    >
                      {(summary?.orders?.revenue_change || 0) >= 0 ? "↑" : "↓"}{" "}
                      {Math.abs(summary?.orders?.revenue_change || 0)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="card-body pt-0">
                <ReactApexChart
                  options={revenueChartOptions}
                  series={revenueChartSeries}
                  type="area"
                  height={300}
                />
              </div>
            </div>
          </div>

          {/* Order Statistics */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Order Statistics</h3>
              </div>
              <div className="card-body pt-0">
                <div className="d-flex flex-column gap-4 mb-4">
                  {[
                    {
                      label: "Pending",
                      value: currentOrders.pending,
                      color: "#3B82F6",
                      icon: "🔄",
                    },
                    {
                      label: "Accepted",
                      value: currentOrders.accepted,
                      color: "#10B981",
                      icon: "✅",
                    },
                    {
                      label: "Delivered",
                      value: currentOrders.delivered,
                      color: "#F59E0B",
                      icon: "📦",
                    },
                    {
                      label: "Returned / Failed",
                      value: currentOrders.returned,
                      color: "#8B5CF6",
                      icon: "↩️",
                    },
                    {
                      label: "Rejected / Cancelled",
                      value:
                        (currentOrders.rejected || 0) +
                        (currentOrders.cancelled || 0),
                      color: "#EF4444",
                      icon: "❌",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="d-flex align-items-center gap-3"
                    >
                      <div
                        className="rounded"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: item.color + "15",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <span
                            className="fw-bold"
                            style={{ color: item.color, fontSize: "18px" }}
                          >
                            {item.icon}
                          </span>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between">
                          <span className="text-gray-700 fw-semibold">
                            {item.label}
                          </span>
                          <span className="fw-bold">
                            {formatCount(item.value)}
                          </span>
                        </div>
                        <div
                          className="progress mt-2"
                          style={{ height: "6px" }}
                        >
                          <div
                            className="progress-bar"
                            style={{
                              width: `${percentOf(item.value, totalOrders)}%`,
                              backgroundColor: item.color,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Most Active Days Chart */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Weekly Order Activity</h3>
              </div>
              <div className="card-body pt-0">
                <ReactApexChart
                  options={orderStatusOptions}
                  series={orderStatusSeries}
                  type="bar"
                  height={300}
                />
              </div>
            </div>
          </div>

          {/* Dealer Performance Gauge */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Dealer Status</h3>
              </div>
              <div className="card-body pt-0 d-flex flex-column justify-content-center">
                <ReactApexChart
                  options={dealerPerformanceOptions}
                  series={dealerPerformanceSeries}
                  type="radialBar"
                  height={250}
                />
                <div className="text-center text-gray-600 fs-7">
                  {formatCount(dealers.active)} active ·{" "}
                  {formatCount(dealers.inactive)} inactive ·{" "}
                  {formatCount(dealers.pending)} pending
                </div>
                <div className="text-center mt-4">
                  <Link
                    to="/pending-dealer"
                    className="btn btn-light-success btn-sm"
                  >
                    Pending approvals ({formatCount(dealers.pending)})
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Best Selling Products */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Best Selling Products</h3>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table table-row-bordered align-middle gs-4">
                    <thead>
                      <tr className="fw-bold text-gray-600 fs-7">
                        <th className="min-w-200px">NAME</th>
                        <th className="min-w-100px">SOLD</th>
                        <th className="min-w-100px">REVENUE</th>
                        <th className="min-w-100px">RATING</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bestSellingProducts.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-gray-500">
                            No products sold in this period
                          </td>
                        </tr>
                      )}
                      {bestSellingProducts.map((product, index) => (
                        <tr key={product.product_id}>
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: "#F3F4F6",
                                  fontSize: "20px",
                                }}
                              >
                                {index + 1}
                              </div>
                              <div>
                                <span className="text-gray-900 fw-bold d-block">
                                  {product.title?.en || product.title?.bn}
                                </span>
                                <span className="text-gray-500 fs-7">
                                  #{product.product_id}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="text-gray-900 fw-semibold">
                              {product.sold.toLocaleString()} sold
                            </span>
                          </td>
                          <td>
                            <span
                              className="fw-bold"
                              style={{ color: "#12B76A" }}
                            >
                              ৳{(Number(product.revenue) || 0).toLocaleString()}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <span className="text-warning">⭐</span>
                              <span className="text-gray-900 fw-semibold">
                                {product.rating}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Order Channel Distribution */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Orders by Channel</h3>
              </div>
              <div className="card-body pt-0">
                <ReactApexChart
                  options={geoDistributionOptions}
                  series={geoDistributionSeries}
                  type="donut"
                  height={280}
                />
              </div>
            </div>
          </div>

          {/* Quick Stats with Real Data */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Quick Stats</h3>
              </div>
              <div className="card-body pt-0">
                <div className="d-flex flex-column gap-4">
                  <div
                    className="d-flex align-items-center justify-content-between p-4 rounded"
                    style={{ backgroundColor: "#F0FDF4" }}
                  >
                    <div>
                      <span className="text-gray-600 fs-7 d-block mb-1">
                        Total Products
                      </span>
                      <span className="text-gray-900 fs-2 fw-bold">
                        {productData?.data?.count || 0}
                      </span>
                    </div>
                    <div className="fs-2x">📦</div>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-between p-4 rounded"
                    style={{ backgroundColor: "#FEF3C7" }}
                  >
                    <div>
                      <span className="text-gray-600 fs-7 d-block mb-1">
                        Total Crops
                      </span>
                      <span className="text-gray-900 fs-2 fw-bold">
                        {cropData?.data?.count || 0}
                      </span>
                    </div>
                    <div className="fs-2x">🌾</div>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-between p-4 rounded"
                    style={{ backgroundColor: "#DBEAFE" }}
                  >
                    <div>
                      <span className="text-gray-600 fs-7 d-block mb-1">
                        Disease Reports
                      </span>
                      <span className="text-gray-900 fs-2 fw-bold">
                        {diseaseInsectData?.data?.count || 0}
                      </span>
                    </div>
                    <div className="fs-2x">🐛</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Recent Activities</h3>
                <div className="card-toolbar">
                  <Link
                    to="/order/pending-order"
                    className="btn btn-sm btn-light"
                  >
                    View All
                  </Link>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="timeline">
                  {[
                    ...(dealers.pending > 0
                      ? [
                          {
                            key: "pending-dealer",
                            icon: "bi-person-plus",
                            color: "#F59E0B",
                            background: "#FEF3C7",
                            title: "Dealer registrations pending approval",
                            subtitle: `${formatCount(
                              dealers.pending
                            )} dealers waiting`,
                            link: "/pending-dealer",
                          },
                        ]
                      : []),
                    ...(summary?.low_stock_count > 0
                      ? [
                          {
                            key: "low-stock",
                            icon: "bi-box-seam",
                            color: "#8B5CF6",
                            background: "#F3E8FF",
                            title: "Low stock alert",
                            subtitle: `${formatCount(
                              summary.low_stock_count
                            )} stock items at or below reorder level`,
                            link: "/stock",
                          },
                        ]
                      : []),
                    ...recentOrders.map((order) => ({
                      key: `order-${order.id}`,
                      icon: "bi-cart-check",
                      color: "#12B76A",
                      background: "#E7F8F0",
                      title: `New order ${order.order_no} · ৳${(
                        Number(order.total_price) || 0
                      ).toLocaleString()}`,
                      // backend dates are Dhaka digits with a Z suffix, so read them as UTC
                      subtitle: `${
                        orderStatusLabel[order.order_status] || "-"
                      } · ${moment
                        .utc(order.created_at)
                        .format("DD MMM YYYY, hh:mm A")}`,
                      link: `/order/order-detail/${order.id}`,
                    })),
                  ].map((activity) => (
                    <div key={activity.key} className="timeline-item">
                      <div className="timeline-icon">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: activity.background,
                          }}
                        >
                          <i
                            className={`bi ${activity.icon} fs-5`}
                            style={{ color: activity.color }}
                          ></i>
                        </div>
                      </div>
                      <div className="timeline-content">
                        <Link
                          to={activity.link}
                          className="fs-6 text-gray-800 text-hover-primary fw-semibold d-block mb-1"
                        >
                          {activity.title}
                        </Link>
                        <span className="fs-7 text-gray-500">
                          {activity.subtitle}
                        </span>
                      </div>
                    </div>
                  ))}
                  {recentOrders.length === 0 && (
                    <span className="text-gray-500">No orders yet</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
